/**
 * Mega menu tabs: ARIA tab switching for desktop dropdown and mobile nav clones.
 */
(function () {
  'use strict';

  const ARROW_KEYS = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1,
  };

  function getMegaMenuHooks() {
    return window.hrMegaMenu || {};
  }

  function refreshMegaMenuLayout() {
    const hooks = getMegaMenuHooks();
    if (typeof hooks.layoutAllCarousels === 'function') {
      hooks.layoutAllCarousels(true);
    }
    if (typeof hooks.refreshDrawerHeight === 'function') {
      hooks.refreshDrawerHeight();
    }
  }

  function announceTabSelection(root, tab) {
    const liveRegion = root.querySelector('[data-mega-menu-tabs-live]');
    if (!liveRegion || !tab) return;
    liveRegion.textContent = tab.textContent.trim() + ' selected';
  }

  function updateViewAll(root, panel) {
    const sharedFooter = root.querySelector('[data-mega-menu-tabs-shared-footer]');
    const sharedLink = root.querySelector('[data-mega-menu-tabs-shared-view-all]');
    const panelFooter = panel?.querySelector('.mega-menu-products__footer');
    const panelLink = panel?.querySelector('.mega-menu-products__view-all');

    if (panelFooter && panelLink) {
      if (sharedFooter) sharedFooter.classList.add('hidden');
      panelFooter.classList.remove('hidden');
      return;
    }

    if (panelFooter) panelFooter.classList.add('hidden');

    if (!sharedFooter || !sharedLink) return;

    const url = panel?.dataset.viewAllUrl || root.dataset.viewAllUrl || '';
    const text = panel?.dataset.viewAllText || root.dataset.viewAllText || '';

    if (url && text) {
      sharedLink.href = url;
      sharedLink.textContent = text;
      sharedFooter.classList.remove('hidden');
    } else {
      sharedFooter.classList.add('hidden');
    }
  }

  function mergePanelSlides(targetPanel, sourcePanel) {
    if (!targetPanel || !sourcePanel) return;

    const targetWrapper = targetPanel.querySelector('.swiper-wrapper');
    const sourceSlides = sourcePanel.querySelectorAll('.swiper-slide');

    if (targetWrapper && sourceSlides.length) {
      sourceSlides.forEach(function (slide) {
        targetWrapper.appendChild(slide);
      });
    }
  }

  function relabelSlides(panel) {
    const slides = panel.querySelectorAll('.swiper-slide');
    slides.forEach(function (slide, idx) {
      slide.setAttribute('aria-label', 'Slide ' + (idx + 1) + ' of ' + slides.length);
    });
  }

  function normalizeTabKey(label) {
    return (label || '').trim().toLowerCase();
  }

  function parseConfiguredTabs(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (label) {
        return label.trim();
      })
      .filter(Boolean);
  }

  function initMegaMenuTabs(root) {
    if (!root || root.dataset.megaMenuTabsInited === 'true') return;
    root.dataset.megaMenuTabsInited = 'true';

    const tablist = root.querySelector('[data-mega-menu-tabs-tablist]');
    const panelsContainer = root.querySelector('[data-mega-menu-tabs-panels]');
    const entries = Array.from(root.querySelectorAll('[data-mega-menu-tab-entry]'));

    if (!tablist || !panelsContainer) return;

    let tabs = [];
    let panels = [];

    if (entries.length) {
      // The parent Tabs block's comma-separated "Tab labels" setting is the
      // source of truth: it defines which tabs exist and their order. Child
      // block entries only attach content to a matching tab. If the setting
      // is empty, fall back to child block labels in block order.
      let configuredTabs = parseConfiguredTabs(root.dataset.tabOrder || '');
      if (!configuredTabs.length) {
        const seen = {};
        entries.forEach(function (entry) {
          const label = (entry.dataset.tabLabel || '').trim();
          const key = normalizeTabKey(label);
          if (!key || seen[key]) return;
          seen[key] = true;
          configuredTabs.push(label);
        });
      }

      // Bucket child entries by normalized label.
      const entriesByKey = {};
      entries.forEach(function (entry) {
        const key = normalizeTabKey(entry.dataset.tabLabel || '');
        if (!key) return;
        (entriesByKey[key] = entriesByKey[key] || []).push(entry);
      });

      configuredTabs.forEach(function (label) {
        const tabKey = normalizeTabKey(label);
        const matched = entriesByKey[tabKey];
        // Configured tabs with no matching content are skipped.
        if (!matched || !matched.length) return;

        let basePanel = null;

        matched.forEach(function (entry) {
          const panel = entry.querySelector('[data-mega-menu-tab-panel]');
          if (!panel) return;

          if (!basePanel) {
            basePanel = panel;
            basePanel.dataset.viewAllUrl = entry.dataset.viewAllUrl || '';
            basePanel.dataset.viewAllText = entry.dataset.viewAllText || '';
          } else {
            mergePanelSlides(basePanel, panel);
            if (!basePanel.dataset.viewAllUrl && entry.dataset.viewAllUrl) {
              basePanel.dataset.viewAllUrl = entry.dataset.viewAllUrl;
            }
            if (!basePanel.dataset.viewAllText && entry.dataset.viewAllText) {
              basePanel.dataset.viewAllText = entry.dataset.viewAllText;
            }
          }
        });

        if (!basePanel) return;

        relabelSlides(basePanel);

        const tab = document.createElement('button');
        tab.type = 'button';
        tab.id = 'mega-menu-tab-btn-' + basePanel.id.replace('mega-menu-tab-panel-', '');
        tab.className = 'mega-menu-tabs__tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', basePanel.id);
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
        tab.textContent = label;
        tablist.appendChild(tab);

        basePanel.setAttribute('aria-labelledby', tab.id);

        tabs.push(tab);
        panels.push(basePanel);
        panelsContainer.appendChild(basePanel);
      });

      // Remove all entry wrappers (including unmatched ones) from the DOM.
      entries.forEach(function (entry) {
        entry.remove();
      });
    } else {
      tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      panels = Array.from(panelsContainer.querySelectorAll('[data-mega-menu-tab-panel]'));

      if (!root.querySelector('[data-mega-menu-tabs-live]')) {
        const liveRegion = document.createElement('div');
        liveRegion.className = 'visually-hidden';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.setAttribute('data-mega-menu-tabs-live', '');
        root.querySelector('.mega-menu-tabs__content')?.prepend(liveRegion);
      }
    }

    if (!tabs.length || !panels.length) return;

    function showPanel(panelId, options) {
      const shouldAnnounce = !options || options.announce !== false;
      let activeTab = null;

      tabs.forEach(function (tab) {
        const isActive = tab.getAttribute('aria-controls') === panelId;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('is-active', isActive);
        if (isActive) activeTab = tab;
      });

      panels.forEach(function (panel) {
        const isActive = panel.id === panelId;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
        panel.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      const activePanel = document.getElementById(panelId);
      updateViewAll(root, activePanel);

      if (shouldAnnounce && activeTab) {
        announceTabSelection(root, activeTab);
      }

      const carouselEl = activePanel?.querySelector('[js-carousel]');
      if (carouselEl?.swiper) carouselEl.swiper.update();

      refreshMegaMenuLayout();
    }

    function handleTabClick(e) {
      const panelId = e.currentTarget.getAttribute('aria-controls');
      if (panelId) showPanel(panelId);
    }

    function handleKeydown(e) {
      const currentIdx = tabs.findIndex(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      });
      if (currentIdx === -1) return;

      let nextIdx = currentIdx;

      if (e.key === 'Home') {
        e.preventDefault();
        nextIdx = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIdx = tabs.length - 1;
      } else {
        const delta = ARROW_KEYS[e.key];
        if (delta === undefined) return;
        e.preventDefault();
        nextIdx = currentIdx + delta;
      }

      if (nextIdx >= 0 && nextIdx < tabs.length && nextIdx !== currentIdx) {
        tabs[nextIdx].click();
        tabs[nextIdx].focus();
      }
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', handleTabClick);
    });
    tablist.addEventListener('keydown', handleKeydown);

    showPanel(tabs[0].getAttribute('aria-controls'), { announce: false });
  }

  function resetMegaMenuTabs(root) {
    if (!root) return;
    delete root.dataset.megaMenuTabsInited;
    initMegaMenuTabs(root);
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-mega-menu-tabs]').forEach(initMegaMenuTabs);
  }

  window.initMegaMenuTabs = initMegaMenuTabs;
  window.initAllMegaMenuTabs = initAll;
  window.resetMegaMenuTabs = resetMegaMenuTabs;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const scope = event.target || document;
    scope.querySelectorAll('[data-mega-menu-tabs]').forEach(resetMegaMenuTabs);
  });
})();
